import { useEffect, useState } from "react";
import { Loader, MultiSelect, Select } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal, ModalActions, ModalContent, ModalTitle } from "@onengine/component-library";

import {
  FileSharesQueryKeys,
  getShareLink,
  getShareLinkOptions,
  listShares,
  resetShareLink,
  revokeShare,
  searchShareRecipients,
  setShareLink,
  shareTargetKey,
  shareWithPeople,
  type FileSharePermission,
  type ShareLinkAudience,
  type ShareLinkPayload,
  type ShareTarget,
} from "@/api/files-hub/fileShares.api";
import { GeneralAccess } from "@/components/features/FileManager/GeneralAccess";
import {
  PERMISSION_OPTIONS,
  permissionLabel,
} from "@/components/features/FileManager/shareLabels.utils";
import { Button } from "@/components/ui/Button/Button.tsx";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal/ConfirmationModal";
import { useActiveSession } from "@/hooks/useActiveSession";
import { modalBody } from "@/components/features/FileManager/Modals/modals.style.ts";
import { getApiErrorMessage } from "@/utils/responseHandler";

import styles from "./ShareModal.module.scss";

function isViewOnlyTarget(target: ShareTarget | null): boolean {
  return (
    target?.kind === "sp_document" ||
    (target?.kind === "record" && target.recordType === "service-providers")
  );
}

export interface ShareDialogProps {
  target: ShareTarget | null;
  name: string;
  onClose: () => void;
}

export function ShareDialog({ target, name, onClose }: ShareDialogProps) {
  const queryClient = useQueryClient();
  const { entityName } = useActiveSession();
  const [isConfirmingReset, setIsConfirmingReset] = useState(false);
  const targetKey = target ? shareTargetKey(target) : null;

  const [userIds, setUserIds] = useState<string[]>([]);
  const [permission, setPermission] = useState<FileSharePermission>("VIEW");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [rolesDraft, setRolesDraft] = useState<string[] | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    setRolesDraft(null);
    setLinkError(null);
    setUserIds([]);
    setPermission("VIEW");
    setSearch("");
    setIsConfirmingReset(false);
  }, [targetKey]);

  const sharesQuery = useQuery({
    queryKey: [FileSharesQueryKeys.shares, targetKey],
    queryFn: () => listShares(target!),
    enabled: !!target,
  });

  const linkQuery = useQuery({
    queryKey: [FileSharesQueryKeys.link, targetKey],
    queryFn: () => getShareLink(target!),
    enabled: !!target,
  });

  const linkOptionsQuery = useQuery({
    queryKey: [FileSharesQueryKeys.link, targetKey, "options"],
    queryFn: () => getShareLinkOptions(target!),
    enabled: !!target,
  });

  const recipientsQuery = useQuery({
    queryKey: [FileSharesQueryKeys.recipients, targetKey, debouncedSearch],
    queryFn: ({ signal }) => searchShareRecipients(target!, debouncedSearch, signal),
    enabled: !!target,
  });

  const viewOnly = isViewOnlyTarget(target) || (sharesQuery.data?.viewOnly ?? false);
  const shares = sharesQuery.data?.items ?? [];

  const refreshShares = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [FileSharesQueryKeys.shares, targetKey] }),
      queryClient.invalidateQueries({ queryKey: [FileSharesQueryKeys.sharedByMe] }),
    ]);

  const refreshLink = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [FileSharesQueryKeys.link, targetKey] }),
      queryClient.invalidateQueries({ queryKey: [FileSharesQueryKeys.sharedByMe] }),
    ]);

  const linkErrorNotice = (title: string, error: unknown) =>
    notifications.show({
      title,
      message: getApiErrorMessage(error, "Could not update the link. Please try again."),
      color: "red",
    });

  const savedAudience = linkQuery.data?.audience ?? "RESTRICTED";
  const savedRoleIds = linkQuery.data?.roleIds ?? [];
  const audience: ShareLinkAudience = rolesDraft ? "ENTITY_ROLES" : savedAudience;
  const roleIds = rolesDraft ?? savedRoleIds;
  const rolesChanged =
    rolesDraft !== null &&
    (savedAudience !== "ENTITY_ROLES" ||
      rolesDraft.length !== savedRoleIds.length ||
      rolesDraft.some((id) => !savedRoleIds.includes(id)));

  const linkMutation = useMutation({
    mutationFn: (payload: ShareLinkPayload) => setShareLink(target!, payload),
    onMutate: () => setLinkError(null),
    onSuccess: () => {
      setRolesDraft(null);
      void refreshLink();
    },
    onError: (error) =>
      setLinkError(getApiErrorMessage(error, "Could not update the link. Please try again.")),
  });

  const changeAudience = (next: ShareLinkAudience) => {
    setLinkError(null);
    if (next === "ENTITY_ROLES") {
      setRolesDraft(savedRoleIds);
      return;
    }
    setRolesDraft(null);
    linkMutation.mutate({ audience: next });
  };

  const resetMutation = useMutation({
    mutationFn: () => resetShareLink(target!),
    onSuccess: () => {
      setIsConfirmingReset(false);
      void refreshLink();
      notifications.show({ message: "Link reset. The old link no longer works.", color: "green" });
    },
    onError: (error) => {
      setIsConfirmingReset(false);
      linkErrorNotice("Link reset failed", error);
    },
  });

  const copyMutation = useMutation({
    mutationFn: async () => {
      const link = linkQuery.data ?? (await setShareLink(target!, { audience: "RESTRICTED" }));
      await navigator.clipboard.writeText(link.url);
    },
    onSuccess: () => {
      void refreshLink();
      notifications.show({ message: "Link copied", color: "green" });
    },
    onError: (error) => linkErrorNotice("Copy link failed", error),
  });

  const shareMutation = useMutation({
    mutationFn: () =>
      shareWithPeople(target!, { userIds, permission: viewOnly ? "VIEW" : permission }),
    onSuccess: () => {
      setUserIds([]);
      void refreshShares();
      notifications.show({ message: `Shared "${name}"`, color: "green" });
    },
    onError: (error) =>
      notifications.show({
        title: "Share failed",
        message: getApiErrorMessage(error, "Could not share this item. Please try again."),
        color: "red",
      }),
  });

  const revokeMutation = useMutation({
    mutationFn: (shareId: string) => revokeShare(target!, shareId),
    onSuccess: () => void refreshShares(),
    onError: (error) =>
      notifications.show({
        title: "Remove access failed",
        message: getApiErrorMessage(error, "Could not remove access. Please try again."),
        color: "red",
      }),
  });

  const sharedUserIds = new Set(shares.map((s) => s.user.id));
  const recipientOptions = (recipientsQuery.data ?? [])
    .filter((u) => !sharedUserIds.has(u.id) || userIds.includes(u.id))
    .map((u) => ({ value: u.id, label: `${u.name} (${u.email})` }));

  return (
    <>
      <Modal open={!!target} onClose={onClose}>
        <ModalContent>
          <ModalTitle>Share &#34;{name}&#34;</ModalTitle>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (userIds.length > 0) shareMutation.mutate();
            }}
          >
            <div style={modalBody}>
              <MultiSelect
                label="People"
                placeholder={userIds.length > 0 ? undefined : "Search people in this entity"}
                data={recipientOptions}
                value={userIds}
                onChange={setUserIds}
                searchable
                clearable
                hidePickedOptions
                filter={({ options }) => options}
                searchValue={search}
                onSearchChange={setSearch}
                nothingFoundMessage={recipientsQuery.isLoading ? "Loading..." : "No people found"}
                comboboxProps={{ withinPortal: true, zIndex: 302 }}
              />
              {viewOnly ? (
                <span className={styles.empty}>
                  Service provider documents are shared view-only.
                </span>
              ) : (
                <Select
                  label="Permission"
                  data={PERMISSION_OPTIONS}
                  value={permission}
                  onChange={(value) => value && setPermission(value as FileSharePermission)}
                  allowDeselect={false}
                  comboboxProps={{ withinPortal: true, zIndex: 302 }}
                />
              )}

              <div className={styles.access}>
                <span className={styles.accessTitle}>People with access</span>
                {sharesQuery.isLoading ? (
                  <Loader size="sm" />
                ) : shares.length === 0 ? (
                  <span className={styles.empty}>Not shared with anyone yet.</span>
                ) : (
                  <ul className={styles.accessList}>
                    {shares.map((share) => (
                      <li key={share.id} className={styles.accessRow}>
                        <div className={styles.person}>
                          <span>{share.user.name}</span>
                          <span className={styles.email}>{share.user.email}</span>
                        </div>
                        <span className={styles.permission}>
                          {permissionLabel(share.permission)}
                        </span>
                        <button
                          type="button"
                          className={styles.remove}
                          aria-label={`Remove access for ${share.user.name}`}
                          onClick={() => revokeMutation.mutate(share.id)}
                          disabled={
                            revokeMutation.isPending && revokeMutation.variables === share.id
                          }
                        >
                          <IconTrash size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <GeneralAccess
                audience={audience}
                entityName={entityName ?? "this entity"}
                options={linkOptionsQuery.data}
                roleIds={roleIds}
                permission={linkQuery.data?.permission}
                error={linkError}
                onAudienceChange={changeAudience}
                onRolesChange={setRolesDraft}
                canSaveRoles={rolesChanged}
                onSaveRoles={() => linkMutation.mutate({ audience: "ENTITY_ROLES", roleIds })}
                onPermissionChange={(next) => linkMutation.mutate({ audience, permission: next })}
                onCopy={() => copyMutation.mutate()}
                onReset={linkQuery.data ? () => setIsConfirmingReset(true) : undefined}
                disabled={linkQuery.isLoading || linkMutation.isPending}
              />

              <ModalActions position={"right"}>
                <Button type="button" variant="neutral-secondary" onClick={onClose}>
                  Done
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={userIds.length === 0 || shareMutation.isPending}
                >
                  Share
                </Button>
              </ModalActions>
            </div>
          </form>
        </ModalContent>
      </Modal>
      <ConfirmationModal
        isOpen={isConfirmingReset}
        onClose={() => setIsConfirmingReset(false)}
        title="Reset this link?"
        body="The current link stops working at once for everyone who has it. People with access can use the new link."
        confirmText="Reset link"
        confirmVariant="destructive-primary"
        isConfirmLoading={resetMutation.isPending}
        onConfirm={() => resetMutation.mutate()}
        zIndex={320}
      />
    </>
  );
}
